'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HEADER_STATE_CHANGE, subscribe, unsubscribe } from 'src/utils/events';
import HTMLRenderer from '../html-renderer/html-renderer.component';
import styles from './page-esi-wrapper.component.module.less';

interface HeaderPlaceholderProps {
  htmlString: string;
}

const HEADER_PLACEHOLDER_ID = 'next_header';
const HEADER_CONTENT_ID = 'next_header_content';
const HEADER_HEIGHT_PRECISION = 100;

function normaliseHeaderHeight(height: number): number {
  return Math.round(height * HEADER_HEIGHT_PRECISION) / HEADER_HEIGHT_PRECISION;
}

function HeaderPlaceholder({ htmlString }: HeaderPlaceholderProps) {
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);
  const updateHeaderHeight = useCallback((height: number) => {
    const nextHeaderHeight = normaliseHeaderHeight(height);

    if (Number.isFinite(nextHeaderHeight) && nextHeaderHeight >= 0) {
      setHeaderHeight(currentHeight =>
        currentHeight === nextHeaderHeight ? currentHeight : nextHeaderHeight,
      );
    }
  }, []);
  const updateMeasuredHeaderHeight = useCallback(
    (height: number) => {
      if (height > 0) {
        updateHeaderHeight(height);
      }
    },
    [updateHeaderHeight],
  );
  const headerPlaceholderStyle = useMemo(() => {
    if (headerHeight === null) {
      return undefined;
    }

    return {
      '--next-header-height': `${headerHeight}px`,
    } as React.CSSProperties;
  }, [headerHeight]);

  useEffect(() => {
    const headerElement =
      document.getElementById(HEADER_CONTENT_ID) ||
      document.getElementById(HEADER_PLACEHOLDER_ID);

    function onHeaderStateChange(e: CustomEvent) {
      const { height } = e.detail as { height: number };

      updateHeaderHeight(height);
    }

    subscribe(HEADER_STATE_CHANGE, onHeaderStateChange);

    if (!headerElement) {
      return () => {
        unsubscribe(HEADER_STATE_CHANGE, onHeaderStateChange);
      };
    }

    const measureHeader = () => {
      updateMeasuredHeaderHeight(
        headerElement.getBoundingClientRect().height ||
          headerElement.offsetHeight,
      );
    };

    measureHeader();

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        unsubscribe(HEADER_STATE_CHANGE, onHeaderStateChange);
      };
    }

    const resizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        updateMeasuredHeaderHeight(entry.contentRect.height);
      });
    });

    resizeObserver.observe(headerElement);

    return () => {
      resizeObserver.disconnect();
      unsubscribe(HEADER_STATE_CHANGE, onHeaderStateChange);
    };
  }, [updateHeaderHeight, updateMeasuredHeaderHeight]);

  return (
    <div
      id={HEADER_PLACEHOLDER_ID}
      className={styles.headerPlaceholder}
      style={headerPlaceholderStyle}>
      <HTMLRenderer
        id={HEADER_CONTENT_ID}
        htmlString={htmlString}
        className={styles.headerContent}
        element="div"
      />
    </div>
  );
}

export default HeaderPlaceholder;
